import React from 'react';
import { useParams } from 'react-router-dom';
import {
  FileText,
  FileSpreadsheet,
  FileImage,
  FileCode,
  FileVideo,
  FileAudio,
  FileArchive,
  FileJson,
  Folder,
  Search,
  Trash2,
  X,
  MessageSquare,
  Plus,
  CheckSquare,
  Square,
} from 'lucide-react';
import { toast } from 'react-hot-toast';

import { Document, Folder as FolderType } from '../types';
import { formatDate } from '../utils/dateUtils';
import { truncateDisplayName } from '../utils/textUtils';

const formatDateLocal = (dateString: string) =>
  formatDate(dateString, { year: 'numeric', month: 'short', day: 'numeric' });

/** Get icon and styles for a document based on filename extension (same as Global Documents / Knowledge Base) */
function getFileTypeIcon(filename: string): {
  Icon: React.ComponentType<{ className?: string }>;
  bgClass: string;
  iconClass: string;
} {
  const ext = (filename || '').split('.').pop()?.toLowerCase() || '';
  const text = ['pdf', 'doc', 'docx', 'rtf', 'odt', 'txt', 'md', 'odf'];
  const spreadsheet = ['xls', 'xlsx', 'ods', 'csv'];
  const image = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico'];
  const code = ['py', 'js', 'ts', 'tsx', 'jsx', 'html', 'css', 'xml', 'yaml', 'yml', 'sh', 'sql'];
  const video = ['mp4', 'webm', 'mov', 'avi', 'mkv'];
  const audio = ['mp3', 'wav', 'ogg', 'm4a', 'flac'];
  const archive = ['zip', 'tar', 'gz', 'rar', '7z'];
  if (ext === 'pdf') return { Icon: FileText, bgClass: 'bg-red-100', iconClass: 'text-red-600' };
  if (ext === 'json') return { Icon: FileJson, bgClass: 'bg-amber-100', iconClass: 'text-amber-600' };
  if (text.includes(ext)) return { Icon: FileText, bgClass: 'bg-blue-100', iconClass: 'text-blue-600' };
  if (spreadsheet.includes(ext)) return { Icon: FileSpreadsheet, bgClass: 'bg-green-100', iconClass: 'text-green-600' };
  if (image.includes(ext)) return { Icon: FileImage, bgClass: 'bg-purple-100', iconClass: 'text-purple-600' };
  if (code.includes(ext)) return { Icon: FileCode, bgClass: 'bg-gray-100', iconClass: 'text-gray-600' };
  if (video.includes(ext)) return { Icon: FileVideo, bgClass: 'bg-pink-100', iconClass: 'text-pink-600' };
  if (audio.includes(ext)) return { Icon: FileAudio, bgClass: 'bg-indigo-100', iconClass: 'text-indigo-600' };
  if (archive.includes(ext)) return { Icon: FileArchive, bgClass: 'bg-yellow-100', iconClass: 'text-yellow-600' };
  return { Icon: FileText, bgClass: 'bg-amber-100', iconClass: 'text-amber-600' };
}

const Documents: React.FC = () => {
  const { folderId } = useParams<{ folderId: string }>();
  const [documents, setDocuments] = React.useState<Document[]>([]);
  const [filteredDocuments, setFilteredDocuments] = React.useState<Document[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [deleteConfirmId, setDeleteConfirmId] = React.useState<string | null>(null);
  const [deletingDocId, setDeletingDocId] = React.useState<string | null>(null);
  const [selectedDocIds, setSelectedDocIds] = React.useState<Set<string>>(new Set());
  const [showBulkFolderSelector, setShowBulkFolderSelector] = React.useState(false);
  const [availableFolders, setAvailableFolders] = React.useState<FolderType[]>([]);
  const [selectedFolderIds, setSelectedFolderIds] = React.useState<string[]>([]);
  const [bulkAssociating, setBulkAssociating] = React.useState(false);

  React.useEffect(() => {
    const fetchDocuments = async () => {
      try {
        setLoading(true);
        const { apiService } = await import('../services/api');
        const docs = await apiService.getDocuments(undefined, folderId);
        setDocuments(docs);
        setFilteredDocuments(docs);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load documents');
      } finally {
        setLoading(false);
      }
    };

    if (folderId) {
      fetchDocuments();
    }
  }, [folderId]);

  // Filter documents based on search query
  React.useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredDocuments(documents);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = documents.filter(
        (doc) =>
          doc.title.toLowerCase().includes(query) ||
          doc.filename.toLowerCase().includes(query) ||
          doc.summary_text?.toLowerCase().includes(query)
      );
      setFilteredDocuments(filtered);
    }
  }, [searchQuery, documents]);

  const getDeleteErrorMessage = (err: unknown): string => {
    if (err && typeof err === 'object' && 'response' in err) {
      const ax = err as { response?: { status?: number; data?: { detail?: string; message?: string } } };
      if (ax.response?.status === 404) {
        return 'Document not found or you don\'t have permission to delete it.';
      }
      const detail = ax.response?.data?.detail ?? ax.response?.data?.message;
      if (typeof detail === 'string') return detail;
      if (typeof detail === 'object' && detail && 'msg' in detail) return String((detail as { msg: string }).msg);
    }
    return err instanceof Error ? err.message : 'Failed to delete document';
  };

  const handleDelete = async (docId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (deleteConfirmId !== docId) {
      setDeleteConfirmId(docId);
      return;
    }
    setDeletingDocId(docId);
    try {
      const { apiService } = await import('../services/api');
      await apiService.deleteDocument(docId);
      toast.success('Document deleted successfully');
      const docs = await apiService.getDocuments(undefined, folderId);
      setDocuments(docs);
      setFilteredDocuments(docs);
      setDeleteConfirmId(null);
    } catch (err) {
      toast.error(getDeleteErrorMessage(err));
    } finally {
      setDeletingDocId(null);
    }
  };

  const cancelDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteConfirmId(null);
  };

  // Bulk selection handlers
  const toggleDocumentSelection = (docId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedDocIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(docId)) {
        newSet.delete(docId);
      } else {
        newSet.add(docId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedDocIds.size === filteredDocuments.length) {
      setSelectedDocIds(new Set());
    } else {
      setSelectedDocIds(new Set(filteredDocuments.map(doc => doc.doc_id)));
    }
  };

  // Fetch folders when bulk selector opens
  React.useEffect(() => {
    const fetchFolders = async () => {
      if (!showBulkFolderSelector) return;
      
      try {
        const { apiService } = await import('../services/api');
        const folders = await apiService.getFolders(true, 'user');
        setAvailableFolders(folders);
        setSelectedFolderIds([]);
      } catch (err) {
        toast.error('Failed to load folders');
        console.error(err);
      }
    };

    fetchFolders();
  }, [showBulkFolderSelector]);

  const handleBulkAssociateFolders = async () => {
    if (selectedDocIds.size === 0 || selectedFolderIds.length === 0) {
      toast('Please select documents and folders', { icon: 'ℹ️' });
      return;
    }

    try {
      setBulkAssociating(true);
      const { apiService } = await import('../services/api');
      const docIdsArray = Array.from(selectedDocIds);
      
      const result = await apiService.bulkAssociateDocumentsWithFolders(docIdsArray, selectedFolderIds);
      
      if (result.success_count > 0) {
        toast.success(`Successfully associated ${result.success_count} document(s) with folder(s)`);
        if (result.failed_count > 0) {
          toast.error(`${result.failed_count} document(s) failed to associate`);
        }
        
        // Refresh documents list
        const docs = await apiService.getDocuments(undefined, folderId);
        setDocuments(docs);
        setFilteredDocuments(docs);
        setSelectedDocIds(new Set());
        setShowBulkFolderSelector(false);
      } else {
        toast.error('Failed to associate documents');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to associate documents';
      toast.error(message);
    } finally {
      setBulkAssociating(false);
    }
  };

  const toggleFolderSelection = (folderId: string) => {
    setSelectedFolderIds(prev => {
      if (prev.includes(folderId)) {
        return prev.filter(id => id !== folderId);
      } else {
        return [...prev, folderId];
      }
    });
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <p className="text-red-600 mb-4">{error}</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          {selectedDocIds.size > 0 && (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
              <span className="text-sm text-gray-600 text-center sm:text-left">
                {selectedDocIds.size} selected
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowBulkFolderSelector(true)}
                  className="flex-1 sm:flex-none inline-flex items-center justify-center px-3 py-1.5 text-xs sm:text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
                >
                  <Plus className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                  <span className="hidden sm:inline">Associate with Folders</span>
                  <span className="sm:hidden">Associate</span>
                </button>
                <button
                  onClick={() => setSelectedDocIds(new Set())}
                  className="flex-1 sm:flex-none inline-flex items-center justify-center px-3 py-1.5 text-xs sm:text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Clear
                </button>
              </div>
            </div>
          )}
        </div>
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 mt-3 sm:mt-4">Documents</h1>
        <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">
          {filteredDocuments.length} document{filteredDocuments.length !== 1 ? 's' : ''} 
          {searchQuery && ` (filtered from ${documents.length} total)`}
          {!searchQuery && ` in this folder`}
        </p>
        
        {/* Search Bar */}
        <div className="mt-3 sm:mt-4 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search documents by title, filename, or content..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* Documents List */}
      <div className="flex-1 overflow-auto px-4 sm:px-6 py-4 sm:py-6">
      {filteredDocuments.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-full text-center">
          <Folder className="h-16 w-16 text-gray-300 mb-4" />
          <p className="text-gray-600 text-lg font-medium">
            {searchQuery ? 'No documents match your search' : 'No documents yet'}
          </p>
          <p className="text-gray-500 mt-2">
            {searchQuery ? 'Try a different search term' : 'Upload documents to see them here'}
          </p>
        </div>
      ) : (
        <>
          {/* Select All Checkbox */}
          {filteredDocuments.length > 0 && (
            <div className="mb-4 flex items-center space-x-2 pb-3 border-b border-gray-200">
              <button
                onClick={toggleSelectAll}
                className="flex items-center space-x-2 text-sm text-gray-700 hover:text-gray-900"
              >
                {selectedDocIds.size === filteredDocuments.length ? (
                  <CheckSquare className="h-5 w-5 text-primary-600" />
                ) : (
                  <Square className="h-5 w-5 text-gray-400" />
                )}
                <span>
                  {selectedDocIds.size === filteredDocuments.length 
                    ? 'Deselect All' 
                    : 'Select All'}
                </span>
              </button>
            </div>
          )}
          
          <div className={`divide-y divide-gray-200 border rounded-lg overflow-hidden bg-white ${filteredDocuments.some((d) => selectedDocIds.has(d.doc_id)) ? 'border-primary-500' : 'border-gray-200'}`}>
            {filteredDocuments.map((doc) => {
              const isSelected = selectedDocIds.has(doc.doc_id);
              const status = doc.status || doc.processing_status || '';
              const isReady = status === 'ready' || status === 'completed';
              const isProcessing = ['processing', 'queued', 'pending', 'indexing', 'uploaded'].includes(status);
              const isFailed = status === 'failed' || status === 'error';
              const isEmpty = status === 'empty';
              const statusLabel = isReady ? 'Ready' : isProcessing ? (status === 'processing' ? 'Processing' : status === 'queued' ? 'Queued' : status) : isFailed ? 'Failed' : isEmpty ? 'Empty' : (status ? status.charAt(0).toUpperCase() + status.slice(1) : '—');
              const { Icon, bgClass, iconClass } = getFileTypeIcon(doc.filename);
              return (
                <div
                  key={doc.id}
                  className={`py-2.5 px-3 flex items-center gap-3 hover:bg-gray-50 transition-colors relative group ${isSelected ? 'bg-primary-50' : ''}`}
                >
                  <div className="flex-shrink-0">
                    <button
                      type="button"
                      onClick={(e) => toggleDocumentSelection(doc.doc_id, e)}
                      className="text-primary-600 hover:text-primary-700"
                    >
                      {isSelected ? <CheckSquare className="h-5 w-5" /> : <Square className="h-5 w-5 text-gray-400" />}
                    </button>
                  </div>
                  <div
                    className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
                    onClick={() => window.open(`/document/${doc.doc_id}`, '_blank')}
                  >
                    <div className={`w-10 h-10 flex-shrink-0 ${bgClass} rounded-lg flex items-center justify-center`}>
                      <Icon className={`h-5 w-5 ${iconClass}`} />
                    </div>
                    <div className="flex-1 min-w-0 max-w-[100ch]">
                      <p className="text-sm font-medium text-gray-900 truncate" title={doc.title}>
                        {truncateDisplayName(doc.title)}
                        <span className="ml-2 text-gray-500 font-normal">· {doc.chunks ?? 0} chunks</span>
                        <span className="ml-2 text-gray-500 font-normal">· {formatDateLocal(doc.created_at)}</span>
                        <span className="ml-2">
                          <span
                            className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                              isReady ? 'bg-green-100 text-green-800' : isProcessing ? 'bg-yellow-100 text-yellow-800' : isFailed ? 'bg-red-100 text-red-800' : isEmpty ? 'bg-gray-100 text-gray-800' : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {statusLabel}
                          </span>
                        </span>
                      </p>
                      {doc.summary_text && (
                        <p className="text-xs text-gray-500 truncate mt-0.5" title={doc.summary_text}>
                          {(doc.summary_text || '').length > 75 ? `${(doc.summary_text || '').slice(0, 75)}…` : doc.summary_text}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    {deleteConfirmId === doc.doc_id ? (
                      <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-2 py-1">
                        <span className="text-xs text-red-700 font-medium">
                          {deletingDocId === doc.doc_id ? 'Deleting…' : 'Delete?'}
                        </span>
                        {deletingDocId !== doc.doc_id && (
                          <>
                            <button type="button" onClick={(e) => handleDelete(doc.doc_id, e)} className="px-2 py-0.5 bg-red-600 text-white text-xs rounded hover:bg-red-700">Yes</button>
                            <button type="button" onClick={(e) => cancelDelete(e)} className="px-2 py-0.5 bg-gray-200 text-gray-700 text-xs rounded hover:bg-gray-300">No</button>
                          </>
                        )}
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => handleDelete(doc.doc_id, e)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                        title="Delete document"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
        )}
      </div>

      {/* Bulk Folder Selector Modal */}
      {showBulkFolderSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                Associate {selectedDocIds.size} Document(s) with Folders
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Select one or more folders to associate with the selected documents
              </p>
            </div>
            
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {availableFolders.length === 0 ? (
                <p className="text-sm text-gray-500 py-4 text-center">No folders available</p>
              ) : (
                <div className="space-y-2">
                  {availableFolders.map((folder) => {
                    const isSelected = selectedFolderIds.includes(folder.id);
                    return (
                      <label
                        key={folder.id}
                        className={`flex items-center px-3 py-2 rounded cursor-pointer hover:bg-gray-50 ${
                          isSelected ? 'bg-primary-50' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleFolderSelection(folder.id)}
                          className="mr-3 h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-900 flex-1">{folder.name}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end space-x-3">
              <button
                onClick={() => {
                  setShowBulkFolderSelector(false);
                  setSelectedFolderIds([]);
                }}
                disabled={bulkAssociating}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <X className="h-4 w-4 mr-1" />
                Cancel
              </button>
              <button
                onClick={handleBulkAssociateFolders}
                disabled={bulkAssociating || selectedFolderIds.length === 0}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <Plus className="h-4 w-4 mr-1" />
                {bulkAssociating ? 'Associating...' : `Associate with ${selectedFolderIds.length} Folder(s)`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Documents;

